import {
  Context,
  ContextValue,
  SortCriterion,
  SortOrder,
  buildCriterionExpression,
  buildFieldSortCriterion,
  buildRelevanceSortCriterion,
} from '@coveo/atomic-react';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';

export const RELEVANCE_SORT = {
  value: 'relevance',
  criterion: buildRelevanceSortCriterion(),
};
export const POPULARITY_SORT = {
  value: 'popularity',
  criterion: buildRelevanceSortCriterion(),
};
export const RATING_SORT = {
  value: 'rating',
  criterion: buildRelevanceSortCriterion(),
};

export const POPULARITY_CONTEXT_KEY = 'popularity';
export const RATING_CONTEXT_KEY = 'rating';

export const RELEVANCY_CRITERION_EXPRESSION = buildCriterionExpression(
  RELEVANCE_SORT.criterion
);

/**
 * Coveo types `ContextValue` as string-only, but the pipeline Conditions these
 * keys drive are boolean-typed and the Search API accepts booleans, so the
 * value has to be cast past the public type.
 */
const addBooleanContext = (context: Context, key: string, value: boolean) =>
  context.add(key, value as unknown as ContextValue);

/**
 * Writes both ranking-mode flags on every call so the two can never drift:
 * there is no path that sets one without clearing the other.
 */
export const applyRankingContext = (
  context: Context | undefined,
  sortValue: string
) => {
  if (!context) return;

  addBooleanContext(
    context,
    POPULARITY_CONTEXT_KEY,
    sortValue === POPULARITY_SORT.value
  );
  addBooleanContext(
    context,
    RATING_CONTEXT_KEY,
    sortValue === RATING_SORT.value
  );
};

/**
 * Relevance, popularity and rating all sort by relevancy, so the sort criterion
 * alone cannot tell them apart. The ranking context is what distinguishes them
 * and it lives on the engine, making it the source of truth every mounted sort
 * control can agree on.
 */
export const getSortValueForRankingContext = (
  contextValues: Record<string, ContextValue> | undefined,
  fallbackValue: string
): string => {
  if (contextValues?.[RATING_CONTEXT_KEY]) return RATING_SORT.value;
  if (contextValues?.[POPULARITY_CONTEXT_KEY]) return POPULARITY_SORT.value;
  return fallbackValue;
};

export const getSortValueForCmsSortOrder = (
  name: string | null | undefined
): string => {
  switch (name) {
    case 'Popularity':
      return POPULARITY_SORT.value;
    case 'Rating':
      return RATING_SORT.value;
    default:
      return RELEVANCE_SORT.value;
  }
};

export const SEARCH_SORT_CRITERIA: {
  value: string;
  criterion: SortCriterion;
}[] = [
  RELEVANCE_SORT,
  POPULARITY_SORT,
  RATING_SORT,
  {
    value: 'published-date',
    criterion: buildFieldSortCriterion(
      'first_published_at',
      SortOrder.Descending
    ),
  },
  {
    value: 'recently-updated',
    criterion: buildFieldSortCriterion(
      'last_published_at',
      SortOrder.Descending
    ),
  },
  {
    value: 'price-low-to-high',
    criterion: buildFieldSortCriterion('ec_price_filter', SortOrder.Ascending),
  },
  {
    value: 'price-high-to-low',
    criterion: buildFieldSortCriterion('ec_price_filter', SortOrder.Descending),
  },
];

export const LISTING_SORT_CRITERIA: {
  value: string;
  criterion: SortCriterion;
}[] = [
  POPULARITY_SORT,
  RATING_SORT,
  {
    value: 'published-date',
    criterion: buildFieldSortCriterion(
      'first_published_at',
      SortOrder.Descending
    ),
  },
  {
    value: 'recently-updated',
    criterion: buildFieldSortCriterion(
      'last_published_at',
      SortOrder.Descending
    ),
  },
  {
    value: 'price-low-to-high',
    criterion: buildFieldSortCriterion('ec_price_filter', SortOrder.Ascending),
  },
  {
    value: 'price-high-to-low',
    criterion: buildFieldSortCriterion('ec_price_filter', SortOrder.Descending),
  },
];

export const getSortCriteriaForSearchHub = (searchHub: string | undefined) => {
  if (searchHub === CoveoSearchHub.SEARCH) {
    return SEARCH_SORT_CRITERIA;
  }
  return LISTING_SORT_CRITERIA;
};
